import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Bell, Check, ClipboardList, HelpCircle, Home, LayoutGrid, Search, Sparkles, X } from "lucide-react";

/*  PRODUCT TOUR — a guided walkthrough that spotlights the actual UI  */
/*  element for each step (sidebar, tabs, bell, help icon), rather     */
/*  than just describing it in a floating card.                       */
/* ------------------------------------------------------------------ */

export interface TourStep {
  title: string;
  description: string;
  icon: LucideIcon;
  tips: string[];
  targetKey: string | null;
}

export const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to FL-Link",
    description: "FL-Link helps your team track, search, and manage HIV/STD case records across Florida facilities. This short tour points out the main areas of the portal — you can restart it anytime.",
    icon: Sparkles,
    tips: [],
    targetKey: null,
  },
  {
    title: "Sidebar navigation",
    description: "Move between Healthcare Lookup, User Management, Facility Management, and System Support from here.",
    icon: Home,
    tips: ["Collapse it to an icon-only rail from the button at the bottom", "On mobile, open it from the menu icon in the top bar"],
    targetKey: "sidebarNav",
  },
  {
    title: "Notifications tab",
    description: "Shows every newly added HIV/STD record in one place, so nothing slips through the cracks.",
    icon: Bell,
    tips: ["A red badge shows how many new records need review"],
    targetKey: "tab-notifications",
  },
  {
    title: "Notification bell",
    description: "Quick access to the same new-record alerts, right from the top bar — no need to switch tabs.",
    icon: Bell,
    tips: ["Click any notification to open that record directly"],
    targetKey: "bell",
  },
  {
    title: "Client Search",
    description: "Search for a client by name, date of birth, birth sex, or source. Switch to Wildcard Search for partial name matches.",
    icon: Search,
    tips: ["Matching records show an address sub-row", "Click Details to open the full record"],
    targetKey: "tab-search",
  },
  {
    title: "Case Registry",
    description: "Browse every case record in one grid — filter by keyword or by how long a record has been pending, and check recent activity without leaving the row.",
    icon: LayoutGrid,
    tips: ["The Activity button shows who created or last updated a record", "The pending-day pills filter the whole grid"],
    targetKey: "tab-lookup",
  },
  {
    title: "Client Details wizard",
    description: "Opening any record walks you through a 13-step wizard — Patient Identification, Contact Info, HIV/STD testing details, and more — ending with a Summary you can review before saving.",
    icon: ClipboardList,
    tips: ["Jump to any step from the left rail at any time", "Use the Edit links on the Summary page to fix anything before saving"],
    targetKey: null,
  },
  {
    title: "Take a tour, anytime",
    description: "That's the whole tour! Come back to this help icon whenever you want to run through it again.",
    icon: HelpCircle,
    tips: [],
    targetKey: "help",
  },
];

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}
export interface Size {
  width: number;
  height: number;
}

/** Pure layout math: given the target's rect, the tooltip's size, and the
 *  viewport, work out where the spotlight cutout and the tooltip should
 *  sit — always clamped fully on-screen. Kept as a standalone function so
 *  it can be tested independently of any real DOM. */
export function computeSpotlightLayout(target: Rect, tooltip: Size, viewport: Size, pad = 8) {
  const spot = {
    top: target.top - pad,
    left: target.left - pad,
    width: target.width + pad * 2,
    height: target.height + pad * 2,
  };

  const spaceBelow = viewport.height - (target.top + target.height + pad);
  const spaceAbove = target.top - pad;

  let tooltipTop: number;
  if (spaceBelow >= tooltip.height + 12 || spaceBelow >= spaceAbove) {
    tooltipTop = target.top + target.height + pad + 12;
  } else {
    tooltipTop = target.top - pad - 12 - tooltip.height;
  }
  tooltipTop = Math.max(12, Math.min(tooltipTop, viewport.height - tooltip.height - 12));

  let tooltipLeft = target.left + target.width / 2 - tooltip.width / 2;
  tooltipLeft = Math.max(12, Math.min(tooltipLeft, viewport.width - tooltip.width - 12));

  return { spot, tooltipTop, tooltipLeft };
}

export const TOUR_TOOLTIP_SIZE: Size = { width: 336, height: 314 };

export interface ProductTourProps {
  open: boolean;
  onClose: () => void;
  getTarget: (key: string) => HTMLElement | null;
}
export function ProductTour({ open, onClose, getTarget }: ProductTourProps) {
  const [step, setStep] = useState<number>(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const current = TOUR_STEPS[step];
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  useEffect(() => {
    if (!open) return;
    function measure() {
      const key = TOUR_STEPS[step].targetKey;
      if (!key) {
        setRect(null);
        return;
      }
      const el = getTarget(key);
      if (!el || typeof el.getBoundingClientRect !== "function") {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step, getTarget]);

  if (!open) return null;

  function finish() {
    onClose();
    setStep(0);
  }
  function next() {
    if (isLast) {
      finish();
      return;
    }
    setStep((s) => s + 1);
  }
  function back() {
    if (!isFirst) setStep((s) => s - 1);
  }

  // Fixed card size — identical on every step, whether or not a live target
  // was found, so the tour never visibly grows or shrinks as you click through.
  const cardBody = (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-start justify-between px-6 pt-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
          <current.icon className="h-5 w-5" />
        </span>
        <button onClick={finish} title="Skip tour" className="text-slate-400 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 pt-4">
        <p className="mb-1 text-xs font-medium text-slate-400">
          Step {step + 1} of {TOUR_STEPS.length}
        </p>
        <h3 className="text-lg font-semibold text-slate-900">{current.title}</h3>
        <p className="mt-1.5 text-sm text-slate-600">{current.description}</p>
        {current.tips.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {current.tips.map((tip) => (
              <li key={tip} className="flex items-start gap-2 text-xs text-slate-500">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-600" />
                {tip}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-slate-100 px-6 py-4">
        <div className="flex gap-1.5">
          {TOUR_STEPS.map((_, i) => (
            <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === step ? "bg-cyan-600" : "bg-slate-200"}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button onClick={back} className="rounded-full border border-slate-300 px-3.5 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Back
            </button>
          )}
          <button onClick={next} className="rounded-full bg-cyan-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-cyan-700">
            {isLast ? "Get started" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );

  // No live target for this step (or target not found) — fall back to a centered modal.
  // Same fixed w-[336px] h-[300px] as the spotlight tooltip below.
  if (!rect) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4">
        <div className="h-[314px] w-[336px] overflow-hidden rounded-2xl bg-white shadow-2xl">{cardBody}</div>
      </div>
    );
  }

  const viewport: Size = { width: window.innerWidth, height: window.innerHeight };
  const { spot, tooltipTop, tooltipLeft } = computeSpotlightLayout(rect, TOUR_TOOLTIP_SIZE, viewport);

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Dimmed backdrop with a cutout around the target, built from four strips */}
      <div className="absolute bg-slate-900/60" style={{ top: 0, left: 0, width: "100%", height: Math.max(spot.top, 0) }} onClick={finish} />
      <div
        className="absolute bg-slate-900/60"
        style={{ top: spot.top + spot.height, left: 0, width: "100%", height: Math.max(viewport.height - (spot.top + spot.height), 0) }}
        onClick={finish}
      />
      <div className="absolute bg-slate-900/60" style={{ top: spot.top, left: 0, width: Math.max(spot.left, 0), height: spot.height }} onClick={finish} />
      <div
        className="absolute bg-slate-900/60"
        style={{ top: spot.top, left: spot.left + spot.width, width: Math.max(viewport.width - (spot.left + spot.width), 0), height: spot.height }}
        onClick={finish}
      />

      {/* Highlight ring around the target */}
      <div
        className="pointer-events-none absolute rounded-lg ring-4 ring-cyan-400"
        style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
      />

      {/* Tooltip card, positioned relative to the target — same fixed size as the centered fallback above */}
      <div className="absolute h-[314px] w-[336px] overflow-hidden rounded-2xl bg-white shadow-2xl" style={{ top: tooltipTop, left: tooltipLeft }}>
        {cardBody}
      </div>
    </div>
  );
}
